import { AlphaCard, Visual } from 'components/Alpha/cards/cardTypes';

export const LIVE_ASSIST_CHANNEL = 'callscribe';

export type LiveAssistStatus = 'listening' | 'paused' | 'no_transcript' | 'budget_reached';

export const LIVE_ASSIST_STATUSES: LiveAssistStatus[] = [
  'listening',
  'paused',
  'no_transcript',
  'budget_reached',
];

export const REPLAY_AVAILABLE_STATUS = 'replay_available';

export const CALL_ENDED_STATUS = 'call_ended';

export const SUPERSEDED_STATUS = 'superseded';

export type LiveAssistStatusValue =
  | LiveAssistStatus
  | typeof REPLAY_AVAILABLE_STATUS
  | typeof CALL_ENDED_STATUS
  | typeof SUPERSEDED_STATUS;

const STATUS_VALUES: LiveAssistStatusValue[] = [
  ...LIVE_ASSIST_STATUSES,
  REPLAY_AVAILABLE_STATUS,
  CALL_ENDED_STATUS,
  SUPERSEDED_STATUS,
];

export const BEHAVIOUR_PATH = 'Alpha/admin/behaviour';

export type LiveAssistCardKind = 'context' | 'past_tickets' | 'docs' | 'action';

export const CONTEXT_CARD_KIND = 'context';

export type LiveAssistFeedback = 'dismiss' | 'pin' | 'unpin' | 'up' | 'down';

export interface LiveAssistCitation {
  title: string;
  url?: string;
  source?: string;
}

export interface LiveAssistAction {
  id: string;
  label: string;
  url?: string;
  ask?: string;
}

export interface LiveAssistCardRequired {
  kind: AlphaCard['kind'];
  toolName: string;
  token: string;
  card: AlphaCard;
}

export interface LiveAssistTicketItem {
  number?: string;
  title?: string;
  status?: string;
  priority?: string;
  age?: string;
  url?: string;
}

export interface LiveAssistConversationItem {
  at?: string;
  direction?: string;
  title?: string;
}

export interface LiveAssistContextDetails {
  ticket?: LiveAssistTicketItem;
  contactName?: string;
  contactPhones: string[];
  companyName?: string;
  openTickets: LiveAssistTicketItem[];
  recentClosedTickets: LiveAssistTicketItem[];
  agreementNames: string[];
  recentConversations: LiveAssistConversationItem[];
  unavailable: string[];
  notices: string[];
}

export interface LiveAssistCard {
  id: string;
  kind: string;
  title: string;
  body?: string;
  citations: LiveAssistCitation[];
  actions: LiveAssistAction[];
  card_required?: LiveAssistCardRequired;
  details?: LiveAssistContextDetails;
  visual?: Visual;
  pinned?: boolean;
}

export type LiveAssistCardPatch = Partial<Omit<LiveAssistCard, 'id'>> & { id: string };

export interface LiveAssistContext {
  crmSourceId?: number;
  ticketId?: string;
  companyId?: string;
  contactId?: string;
}

export type LiveAssistEvent =
  | {
      type: 'session_started';
      sessionId?: number;
      streamId?: string;
      hasTranscript?: boolean;
      context: LiveAssistContext;
      replayAvailable: boolean;
    }
  | { type: 'card'; card: LiveAssistCard }
  | { type: 'card_update'; card: LiveAssistCardPatch }
  | { type: 'card_removed'; id: string }
  | { type: 'card_done'; id: string; label?: string }
  | { type: 'ask_delta'; clientAskId?: string; askId?: string; text: string }
  | { type: 'ask_done'; clientAskId?: string; askId?: string; citations: LiveAssistCitation[] }
  | { type: 'status'; status: LiveAssistStatusValue };

export interface LiveAssistPaths {
  stream: string;
  ask: string;
  pause: string;
  resume: string;
  replay: string;
  feedback: (cardId: string) => string;
}

export const liveAssistPaths = (channel: string, conversationId: number): LiveAssistPaths => {
  const base = `alpha/live-assist/${channel}/${conversationId}`;
  return {
    stream: `${base}/stream`,
    ask: `${base}/ask`,
    pause: `${base}/pause`,
    resume: `${base}/resume`,
    replay: `${base}/replay`,
    feedback: cardId => `${base}/cards/${encodeURIComponent(cardId)}/feedback`,
  };
};

type Raw = Record<string, unknown>;

const isRecord = (value: unknown): value is Raw =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const text = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() !== '' ? value : undefined;

const idOf = (value: unknown): string | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return text(value);
};

const SAFE_PROTOCOLS = ['http:', 'https:'];

const STREAM_ID_PATTERN = /^[0-9a-f]{32}$/i;

export const safeHref = (value: unknown): string | undefined => {
  const href = text(value);
  if (href === undefined) return undefined;
  try {
    const url = new URL(href, window.location.origin);
    return SAFE_PROTOCOLS.includes(url.protocol) ? url.href : undefined;
  } catch {
    return undefined;
  }
};

const withOptional = <T extends object>(target: T, key: string, value: unknown): T =>
  value === undefined ? target : { ...target, [key]: value };

const toCitation = (raw: unknown): LiveAssistCitation | undefined => {
  if (!isRecord(raw)) return undefined;
  const title = text(raw.title);
  if (title === undefined) return undefined;
  const withUrl = withOptional<LiveAssistCitation>({ title }, 'url', safeHref(raw.url));
  return withOptional(withUrl, 'source', text(raw.source));
};

const toAction = (raw: unknown): LiveAssistAction | undefined => {
  if (!isRecord(raw)) return undefined;
  const id = idOf(raw.id);
  const label = text(raw.label);
  if (id === undefined || label === undefined) return undefined;
  const url = safeHref(raw.url);
  const ask = text(raw.ask);
  if (url === undefined && ask === undefined) return undefined;
  const withUrl = withOptional<LiveAssistAction>({ id, label }, 'url', url);
  return withOptional(withUrl, 'ask', ask);
};

const listOf = <T>(raw: unknown, map: (item: unknown) => T | undefined): T[] =>
  Array.isArray(raw) ? raw.map(map).filter((item): item is T => item !== undefined) : [];

const CARD_KINDS: unknown[] = ['confirm', 'form', 'ask'];

const toCardRequired = (raw: unknown): LiveAssistCardRequired | undefined => {
  if (!isRecord(raw) || !isRecord(raw.card)) return undefined;
  const token = text(raw.token);
  const toolName = text(raw.toolName) ?? text(raw.card.toolName);
  const kind = CARD_KINDS.includes(raw.kind) ? raw.kind : raw.card.kind;
  if (token === undefined || toolName === undefined || !CARD_KINDS.includes(kind)) {
    return undefined;
  }
  return {
    kind: kind as AlphaCard['kind'],
    toolName,
    token,
    card: raw.card as unknown as AlphaCard,
  };
};

const toVisual = (raw: unknown): Visual | undefined =>
  isRecord(raw) && typeof raw.id === 'string' && Array.isArray(raw.rows)
    ? (raw as unknown as Visual)
    : undefined;

const cardRequiredOf = (raw: Raw): unknown => raw.card_required ?? raw.cardRequired ?? raw.card;

const texts = (raw: unknown): string[] =>
  listOf(raw, item => (typeof item === 'string' ? text(item) : undefined));

const toTicketItem = (raw: unknown): LiveAssistTicketItem | undefined => {
  if (!isRecord(raw)) return undefined;
  const item: LiveAssistTicketItem = {};
  const number = idOf(raw.number);
  const title = text(raw.title);
  if (number === undefined && title === undefined) return undefined;
  if (number !== undefined) item.number = number;
  if (title !== undefined) item.title = title;
  const status = text(raw.status);
  const priority = text(raw.priority);
  const age = text(raw.age);
  const url = safeHref(raw.url);
  if (status !== undefined) item.status = status;
  if (priority !== undefined) item.priority = priority;
  if (age !== undefined) item.age = age;
  if (url !== undefined) item.url = url;
  return item;
};

const toConversationItem = (raw: unknown): LiveAssistConversationItem | undefined => {
  if (!isRecord(raw)) return undefined;
  const item: LiveAssistConversationItem = {};
  const at = text(raw.at);
  const direction = text(raw.direction);
  const title = text(raw.title);
  if (at === undefined && title === undefined) return undefined;
  if (at !== undefined) item.at = at;
  if (direction !== undefined) item.direction = direction;
  if (title !== undefined) item.title = title;
  return item;
};

const nameOf = (raw: unknown): string | undefined => (isRecord(raw) ? text(raw.name) : undefined);

const toContextDetails = (raw: unknown): LiveAssistContextDetails | undefined => {
  if (!isRecord(raw)) return undefined;
  const details: LiveAssistContextDetails = {
    contactPhones: isRecord(raw.contact) ? texts(raw.contact.phones) : [],
    openTickets: listOf(raw.openTickets, toTicketItem),
    recentClosedTickets: listOf(raw.recentClosedTickets, toTicketItem),
    agreementNames: isRecord(raw.agreement) ? texts(raw.agreement.names) : [],
    recentConversations: listOf(raw.recentConversations, toConversationItem),
    unavailable: texts(raw.unavailable),
    notices: texts(raw.notices),
  };
  const ticket = toTicketItem(raw.ticket);
  const contactName = nameOf(raw.contact);
  const companyName = nameOf(raw.company);
  if (ticket !== undefined) details.ticket = ticket;
  if (contactName !== undefined) details.contactName = contactName;
  if (companyName !== undefined) details.companyName = companyName;
  return details;
};

const cardFields = (raw: Raw): Partial<Omit<LiveAssistCard, 'id'>> => {
  const fields: Partial<Omit<LiveAssistCard, 'id'>> = {};
  const kind = text(raw.kind);
  const title = text(raw.title);
  if (kind !== undefined) fields.kind = kind;
  if (title !== undefined) fields.title = title;
  if (typeof raw.body === 'string') fields.body = raw.body;
  if (Array.isArray(raw.citations)) fields.citations = listOf(raw.citations, toCitation);
  if (Array.isArray(raw.actions)) fields.actions = listOf(raw.actions, toAction);
  const required = toCardRequired(cardRequiredOf(raw));
  if (required !== undefined) fields.card_required = required;
  const details = kind === CONTEXT_CARD_KIND ? toContextDetails(raw.card) : undefined;
  if (details !== undefined) fields.details = details;
  const visual = toVisual(raw.visual) ?? toVisual(raw.card);
  if (visual !== undefined) fields.visual = visual;
  if (typeof raw.pinned === 'boolean') fields.pinned = raw.pinned;
  return fields;
};

const toCard = (raw: Raw): LiveAssistCard | undefined => {
  const id = idOf(raw.id);
  const fields = cardFields(raw);
  if (id === undefined || fields.title === undefined) return undefined;
  return {
    id,
    kind: fields.kind ?? 'suggestion',
    citations: [],
    actions: [],
    ...fields,
    title: fields.title,
  };
};

const toContext = (raw: unknown): LiveAssistContext => {
  if (!isRecord(raw)) return {};
  const context: LiveAssistContext = {};
  const ticketId = idOf(raw.ticketId);
  const companyId = idOf(raw.companyId);
  const contactId = idOf(raw.contactId);
  if (typeof raw.crmSourceId === 'number') context.crmSourceId = raw.crmSourceId;
  if (ticketId !== undefined) context.ticketId = ticketId;
  if (companyId !== undefined) context.companyId = companyId;
  if (contactId !== undefined) context.contactId = contactId;
  return context;
};

const toStatus = (raw: Raw): LiveAssistStatusValue | undefined => {
  const value = raw.status ?? raw.state;
  return STATUS_VALUES.find(status => status === value);
};

const askIds = (raw: Raw): { clientAskId?: string; askId?: string } => {
  const clientAskId = idOf(raw.clientAskId);
  const askId = idOf(raw.askId);
  return {
    ...(clientAskId !== undefined ? { clientAskId } : {}),
    ...(askId !== undefined ? { askId } : {}),
  };
};

export const normalizeLiveAssistEvent = (raw: unknown): LiveAssistEvent | undefined => {
  if (!isRecord(raw)) return undefined;
  switch (raw.type) {
    case 'session_started': {
      const sessionId = typeof raw.sessionId === 'number' ? raw.sessionId : undefined;
      const streamId =
        typeof raw.streamId === 'string' && STREAM_ID_PATTERN.test(raw.streamId)
          ? raw.streamId
          : undefined;
      return {
        type: 'session_started',
        ...(sessionId !== undefined ? { sessionId } : {}),
        ...(streamId !== undefined ? { streamId } : {}),
        ...(typeof raw.hasTranscript === 'boolean' ? { hasTranscript: raw.hasTranscript } : {}),
        context: toContext(raw.context),
        replayAvailable: raw.replayAvailable === true,
      };
    }
    case 'card': {
      const card = toCard(raw);
      return card === undefined ? undefined : { type: 'card', card };
    }
    case 'card_update': {
      const id = idOf(raw.id);
      return id === undefined
        ? undefined
        : { type: 'card_update', card: { id, ...cardFields(raw) } };
    }
    case 'card_removed': {
      const id = idOf(raw.id);
      return id === undefined ? undefined : { type: 'card_removed', id };
    }
    case 'card_done': {
      const id = idOf(raw.id);
      const label = text(raw.label);
      if (id === undefined) return undefined;
      return label === undefined ? { type: 'card_done', id } : { type: 'card_done', id, label };
    }
    case 'ask_delta': {
      const delta = typeof raw.text === 'string' ? raw.text : raw.delta;
      if (typeof delta !== 'string') return undefined;
      return { type: 'ask_delta', ...askIds(raw), text: delta };
    }
    case 'ask_done':
      return { type: 'ask_done', ...askIds(raw), citations: listOf(raw.citations, toCitation) };
    case 'status': {
      const status = toStatus(raw);
      return status === undefined ? undefined : { type: 'status', status };
    }
    default:
      return undefined;
  }
};

const parseFrame = (frame: string): LiveAssistEvent | undefined => {
  let eventName: string | undefined;
  const data: string[] = [];
  frame.split('\n').forEach(line => {
    if (line.startsWith('event:')) eventName = line.slice(6).trim();
    if (line.startsWith('data:')) data.push(line.slice(5).trim());
  });
  if (data.length === 0) return undefined;
  try {
    const parsed: unknown = JSON.parse(data.join('\n'));
    if (!isRecord(parsed)) return undefined;
    const typed =
      parsed.type === undefined && eventName !== undefined
        ? { ...parsed, type: eventName }
        : parsed;
    return normalizeLiveAssistEvent(typed);
  } catch {
    return undefined;
  }
};

export const parseLiveAssistChunk = (
  buffer: string,
): { events: LiveAssistEvent[]; rest: string } => {
  const frames = buffer.replace(/\r\n/g, '\n').split('\n\n');
  const rest = frames.pop() ?? '';
  return {
    events: frames.map(parseFrame).filter((event): event is LiveAssistEvent => event !== undefined),
    rest,
  };
};
